import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Search, Calendar, Heart, Sparkles } from 'lucide-react';

const STEPS_AR = [
  { icon: Search, title: 'استكشف الأنشطة', body: 'تصفح ورش وفعاليات حق البيت والديوانية في كل أنحاء الكويت.' },
  { icon: Calendar, title: 'احجز بسهولة', body: 'اختر التاريخ والوقت والموقع، وادفع بأمان من خلال K-Net أو البطاقات.' },
  { icon: Heart, title: 'احفظ المفضلة', body: 'احفظ الأنشطة اللي عجبتك، وشوف عروض اليوم 🔥 على الصفحة الرئيسية.' },
  { icon: Sparkles, title: 'تابع تجربتك', body: 'كل حجوزاتك، فواتيرك، وتقييماتك في مكان واحد بلوحة التحكم.' },
];
const STEPS_EN = [
  { icon: Search, title: 'Discover Activities', body: 'Browse in-home workshops and events across Kuwait.' },
  { icon: Calendar, title: 'Book Easily', body: 'Pick date, time, and location. Pay securely via K-Net or cards.' },
  { icon: Heart, title: 'Save Favorites', body: 'Save activities you love and check daily flash deals 🔥.' },
  { icon: Sparkles, title: 'Track Everything', body: 'All your bookings, receipts, and reviews in one dashboard.' },
];

export default function OnboardingTour() {
  const { user } = useAuth();
  const { locale } = useLocale();
  const isArabic = locale === 'ar';
  const steps = isArabic ? STEPS_AR : STEPS_EN;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('profiles')
        .select('onboarding_completed_at')
        .eq('id', user.id)
        .maybeSingle();
      if (!cancelled && data && !data.onboarding_completed_at) {
        setOpen(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const finish = async () => {
    setOpen(false);
    if (user) {
      await supabase
        .from('profiles')
        .update({ onboarding_completed_at: new Date().toISOString() })
        .eq('id', user.id);
    }
  };

  const Current = steps[step];
  const Icon = Current.icon;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(); }}>
      <DialogContent className="max-w-md text-center p-8">
        <div className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
          <Icon className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-xl font-bold mb-2">{Current.title}</h2>
        <p className="text-sm text-muted-foreground mb-6">{Current.body}</p>
        <div className="flex items-center justify-center gap-1 mb-4">
          {steps.map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-primary' : 'w-1.5 bg-muted'}`} />
          ))}
        </div>
        <div className="flex gap-2 justify-center">
          <Button variant="ghost" onClick={finish}>
            {isArabic ? 'تخطّي' : 'Skip'}
          </Button>
          <Button onClick={() => step < steps.length - 1 ? setStep(step + 1) : finish()}>
            {step < steps.length - 1 ? (isArabic ? 'التالي' : 'Next') : (isArabic ? 'ابدأ' : 'Get started')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
